import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Token from "src/util/Token";
import { alertSlice } from "src/store/alert/alert.slice";
import { authSlice } from "src/store/auth/auth.slice";

const WARN_BEFORE_SECONDS = 5 * 60; // 5 minutes

/**
 * Watches the JWT expiry and:
 *  - Shows a warning alert 5 minutes before expiry (or immediately if < 5 min remain)
 *  - Clears auth state and redirects to /login on expiry
 *
 * Mount once at the top level of authenticated routes.
 */
export function useTokenExpiry() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const warnTimerRef = useRef(null);
  const expireTimerRef = useRef(null);

  useEffect(() => {
    // Both helpers are defined inside the effect so dispatch/navigate are in
    // scope and react-hooks/exhaustive-deps is satisfied without suppressions.
    function showWarning() {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          severity: "warning",
          message: "Your session will expire in 5 minutes. Please save your work.",
        })
      );
    }

    function handleExpired() {
      Token.clear();
      dispatch(authSlice.actions.setAuth(null));
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          severity: "error",
          message: "Your session has expired. Please log in again.",
        })
      );
      navigate("/login", { replace: true });
    }

    const secondsLeft = Token.secondsUntilExpiry();

    if (secondsLeft <= 0) {
      handleExpired();
      return;
    }

    const warnInMs = (secondsLeft - WARN_BEFORE_SECONDS) * 1000;

    if (warnInMs <= 0) {
      // Less than 5 minutes remaining — show warning immediately instead of
      // silently skipping it
      showWarning();
    } else {
      warnTimerRef.current = setTimeout(showWarning, warnInMs);
    }

    expireTimerRef.current = setTimeout(handleExpired, secondsLeft * 1000);

    return () => {
      clearTimeout(warnTimerRef.current);
      clearTimeout(expireTimerRef.current);
    };
  }, [dispatch, navigate]);
}
